// HeyGen's prompt-to-avatar endpoint only accepts a single `prompt` string —
// the structured form fields (gender, ethnicity, etc.) are NOT separate API
// parameters. This helper composes them into the prompt so the structured
// dropdowns actually influence generation.
//
// Used by both ActorsDialog (Avatar Lab tab) and the standalone Avatar Design
// page. Same form (AvatarCreationModal / LookCreationModal) feeds both flows.

interface AvatarFormFields {
  age?: string | null;
  gender?: string | null;
  ethnicity?: string | null;
  pose?: string | null;
  style?: string | null;
  appearance?: string | null;
}

const isSet = (v: string | null | undefined): v is string =>
  !!v && v !== "Unspecified";

const normalizeGender = (g: string): string | null => {
  const v = g.toLowerCase();
  if (v === "woman" || v === "female") return "woman";
  if (v === "man" || v === "male") return "man";
  return null;
};

const humanizePose = (p: string): string =>
  p.replace(/_/g, " ").trim();

export const buildAvatarPrompt = (form: AvatarFormFields): string => {
  const personParts: string[] = [];
  if (isSet(form.age)) personParts.push(form.age.toLowerCase());
  if (isSet(form.ethnicity)) personParts.push(form.ethnicity);
  if (isSet(form.gender)) {
    const g = normalizeGender(form.gender);
    if (g) personParts.push(g);
  }
  const personPhrase = personParts.length > 0 ? `A ${personParts.join(" ")}.` : "";

  const styleParts: string[] = [];
  if (isSet(form.pose)) styleParts.push(`${humanizePose(form.pose)} shot`);
  if (isSet(form.style)) styleParts.push(`${form.style.toLowerCase()} style`);
  const stylePhrase = styleParts.length > 0 ? `${styleParts.join(", ")}.` : "";

  const appearance = (form.appearance || "").trim();

  return [personPhrase, stylePhrase, appearance].filter(Boolean).join(" ").trim();
};
